/**
 * CompanyPostsSection — iter332
 * ────────────────────────────────
 * Visual "activity posts" surface on the public company profile.
 * Renders three states:
 *   1. Owner + no posts → premium empty state with a "Create post" CTA.
 *   2. Any viewer  + posts → responsive card grid + swipeable lightbox.
 *   3. Visitor + no posts → nothing (section stays out of the way).
 *
 * Design intent:
 *   • Cohesive with CARTAL's ivory/gold/onyx palette — no new palette.
 *   • Mobile-first + RTL aware (uses logical ms-/me-* spacing).
 *   • Images own the visual real-estate; chrome is minimal.
 *   • Multi-image posts show a soft counter chip in the corner and open
 *     into a full-viewport lightbox with left/right/keyboard nav.
 *
 * Explicitly NOT a vehicle listing composer — no make/model/year/price,
 * no city, no vehicle type. Uses the existing `/api/upload` endpoint
 * that already serves the marketplace, so no new upload plumbing.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, ImagePlus, Trash2, X, ChevronLeft, ChevronRight, Camera,
  Loader2, Sparkles,
} from "lucide-react";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";

const MAX_IMAGES = 10;

export default function CompanyPostsSection({ company }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const isOwner = !!(user && company && company.owner_user_id === user.id);
  const isSuper = !!(user && user.role === "super_admin");
  const canManage = isOwner || isSuper;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {post, index}

  const load = () => {
    if (!company?.slug) return;
    setLoading(true);
    apiClient
      .get(`/platform/public/companies/${company.slug}/posts`)
      .then(({ data }) => setPosts(data.items || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [company?.slug]);

  const onDelete = async (post) => {
    const yes = window.confirm(
      lang === "ar" ? "حذف هذا المنشور؟" : "Delete this post?"
    );
    if (!yes) return;
    try {
      await apiClient.delete(`/platform/business-posts/${post.id}`);
      setPosts((p) => p.filter((x) => x.id !== post.id));
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || (lang === "ar" ? "تعذّر الحذف" : "Delete failed")
      );
    }
  };

  // Visitor with no posts → hide section entirely (no empty state chrome).
  if (!canManage && !loading && posts.length === 0) return null;

  return (
    <section
      data-testid="company-posts-section"
      className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden"
    >
      {/* Header — same visual weight as the sibling Card headers */}
      <header
        className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 pb-3
                   border-b border-[#0a0a0a]/[0.06]"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-flex w-8 h-8 rounded-lg items-center justify-center flex-none"
            style={{ background: "linear-gradient(135deg,#f5e9c9,#e8c873)" }}
          >
            <Sparkles className="w-4 h-4 text-[#0a0a0a]" />
          </span>
          <h3 className="font-display text-base sm:text-lg text-[#0a0a0a] truncate">
            {lang === "ar" ? "منشورات النشاط" : "Activity posts"}
          </h3>
          {posts.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#8a6f2e] px-1.5 py-0.5 rounded-full bg-[#f5e9c9]/40">
              {posts.length}
            </span>
          )}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            data-testid="posts-open-composer"
            className="text-[12px] font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
            style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
          >
            <Plus className="w-3.5 h-3.5" />
            {lang === "ar" ? "إضافة منشور" : "New post"}
          </button>
        )}
      </header>

      {/* Body */}
      <div className="p-4 sm:p-5">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" aria-busy>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-xl bg-[#f0eee7] animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <PostsEmptyState lang={lang} canManage={canManage} onCreate={() => setComposerOpen(true)} />
        ) : (
          <PostsGrid
            posts={posts}
            lang={lang}
            canManage={canManage}
            onOpen={(post, index) => setLightbox({ post, index })}
            onDelete={onDelete}
          />
        )}
      </div>

      {composerOpen && (
        <PostComposer
          businessId={company.id}
          lang={lang}
          onClose={() => setComposerOpen(false)}
          onCreated={(post) => {
            setPosts((p) => [post, ...p]);
            setComposerOpen(false);
          }}
        />
      )}

      {lightbox && (
        <PostLightbox
          post={lightbox.post}
          startIndex={lightbox.index}
          lang={lang}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}

/* ─── Empty state ──────────────────────────────────────────────────── */
function PostsEmptyState({ lang, canManage, onCreate }) {
  return (
    <div
      data-testid="posts-empty-state"
      className="rounded-xl border border-dashed border-[#c9a55c]/40 bg-[#fbf8ef]/60 px-5 py-8 sm:py-10 text-center"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-white border border-[#c9a55c]/40 flex items-center justify-center mb-3">
        <Camera className="w-5 h-5 text-[#8a6f2e]" />
      </div>
      <div className="font-display text-lg text-[#0a0a0a] mb-1">
        {lang === "ar" ? "اعرض أعمالك بصورة احترافية" : "Showcase your work"}
      </div>
      <p className="text-[13px] text-[#6b6b6b] max-w-md mx-auto leading-relaxed">
        {lang === "ar"
          ? "أضف صور أعمالك السابقة، العروض، البروشورات، أو لقطات من داخل المركز — لتترك انطباعًا فوريًا لدى الزائرين."
          : "Add photos of past work, offers, brochures or in-house moments — make a strong first impression on every visitor."}
      </p>
      {canManage && (
        <button
          type="button"
          onClick={onCreate}
          data-testid="posts-empty-create"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-[13px]"
          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
        >
          <Plus className="w-4 h-4" />
          {lang === "ar" ? "أضف أول منشور" : "Create first post"}
        </button>
      )}
    </div>
  );
}

/* ─── Grid ─────────────────────────────────────────────────────────── */
function PostsGrid({ posts, lang, canManage, onOpen, onDelete }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          lang={lang}
          canManage={canManage}
          onOpen={onOpen}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function PostCard({ post, lang, canManage, onOpen, onDelete }) {
  const cover = post.images?.[0];
  const count = post.images?.length || 0;
  const dateShort = (post.created_at || "").slice(0, 10);
  return (
    <div
      className="group relative rounded-xl overflow-hidden bg-[#f0eee7] border border-[#0a0a0a]/[0.05]"
      data-testid={`post-card-${post.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(post, 0)}
        className="block w-full aspect-[4/5] focus:outline-none"
        aria-label={lang === "ar" ? "فتح المنشور" : "Open post"}
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#c9a55c]">
            <Camera className="w-8 h-8" />
          </div>
        )}
      </button>
      {/* image-count chip */}
      {count > 1 && (
        <div
          className="absolute top-2 end-2 rounded-full bg-black/60 backdrop-blur-sm text-white
                     text-[10px] font-bold px-2 py-1 inline-flex items-center gap-1"
        >
          <ImagePlus className="w-3 h-3" />
          {count}
        </div>
      )}
      {/* bottom caption strip */}
      {(post.caption || dateShort) && (
        <div
          className="absolute inset-x-0 bottom-0 px-2.5 py-2 text-white
                     bg-gradient-to-t from-black/70 via-black/30 to-transparent"
        >
          {post.caption && (
            <div className="text-[12px] leading-snug line-clamp-2 font-medium">
              {post.caption}
            </div>
          )}
          {dateShort && (
            <div className="text-[10px] opacity-70 mt-0.5">{dateShort}</div>
          )}
        </div>
      )}
      {canManage && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(post); }}
          data-testid={`post-delete-${post.id}`}
          className="absolute top-2 start-2 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600
                     text-white flex items-center justify-center transition-colors"
          aria-label={lang === "ar" ? "حذف" : "Delete"}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/* ─── Composer (create) ────────────────────────────────────────────── */
function PostComposer({ businessId, lang, onClose, onCreated }) {
  const [caption, setCaption] = useState("");
  const [items, setItems] = useState([]); // {file, previewUrl, url|null, uploading, error}
  const [publishing, setPublishing] = useState(false);
  const fileRef = useRef(null);

  const canPublish =
    items.length > 0 &&
    items.every((it) => !!it.url && !it.uploading && !it.error) &&
    !publishing;

  const onPick = () => fileRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const remainingSlots = MAX_IMAGES - items.length;
    const accepted = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.error(
        lang === "ar"
          ? `الحد الأقصى ${MAX_IMAGES} صور لكل منشور`
          : `Max ${MAX_IMAGES} images per post`
      );
    }
    const drafts = accepted.map((f) => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
      url: null,
      uploading: true,
      error: null,
    }));
    setItems((prev) => [...prev, ...drafts]);
    // Upload sequentially so status feedback is easy to reason about.
    for (const draft of drafts) {
      try {
        const fd = new FormData();
        fd.append("file", draft.file);
        const { data } = await apiClient.post("/upload?kind=business_post", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = data?.url || data?.path;
        setItems((prev) =>
          prev.map((it) =>
            it.previewUrl === draft.previewUrl
              ? { ...it, url, uploading: false }
              : it
          )
        );
      } catch (err) {
        setItems((prev) =>
          prev.map((it) =>
            it.previewUrl === draft.previewUrl
              ? {
                  ...it,
                  uploading: false,
                  error:
                    err?.response?.data?.detail ||
                    (lang === "ar" ? "فشل الرفع" : "Upload failed"),
                }
              : it
          )
        );
      }
    }
  };

  const removeItem = (previewUrl) => {
    setItems((prev) => {
      const gone = prev.find((it) => it.previewUrl === previewUrl);
      if (gone) URL.revokeObjectURL(previewUrl);
      return prev.filter((it) => it.previewUrl !== previewUrl);
    });
  };

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const imgs = items.filter((it) => it.url).map((it) => it.url);
      const { data } = await apiClient.post(
        `/platform/businesses/${businessId}/posts`,
        { images: imgs, caption: caption.trim() || undefined }
      );
      toast.success(lang === "ar" ? "تم النشر" : "Published");
      onCreated(data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          (lang === "ar" ? "تعذّر النشر" : "Publish failed")
      );
    } finally {
      setPublishing(false);
    }
  };

  // Cleanup blob URLs on unmount.
  useEffect(
    () => () => {
      items.forEach((it) => URL.revokeObjectURL(it.previewUrl));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/60"
      />
      <div
        className="fixed inset-x-3 top-8 sm:inset-x-auto sm:right-1/2 sm:translate-x-1/2 sm:max-w-lg
                   z-50 rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        data-testid="posts-composer"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#0a0a0a]/[0.06]">
          <h4 className="font-display text-lg">
            {lang === "ar" ? "منشور جديد" : "New post"}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5"
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 600))}
            placeholder={
              lang === "ar"
                ? "وصف مختصر (اختياري) — العرض، الخدمة، أو ما تريد إبرازه…"
                : "Short caption (optional) — the offer, service, or highlight…"
            }
            rows={3}
            data-testid="posts-composer-caption"
            className="w-full rounded-xl border border-[#0a0a0a]/[0.1] px-3 py-2 text-sm resize-none
                       focus:outline-none focus:border-[#c9a55c]"
          />

          <div className="grid grid-cols-3 gap-2">
            {items.map((it) => (
              <div
                key={it.previewUrl}
                className="relative aspect-square rounded-lg overflow-hidden bg-[#f0eee7]"
              >
                <img src={it.previewUrl} alt="" className="w-full h-full object-cover" />
                {it.uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                )}
                {it.error && (
                  <div className="absolute inset-0 bg-red-600/70 text-white text-[10px] p-1 flex items-center justify-center text-center">
                    {it.error}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeItem(it.previewUrl)}
                  className="absolute top-1 end-1 w-6 h-6 rounded-full bg-black/60 hover:bg-red-600
                             text-white flex items-center justify-center"
                  aria-label={lang === "ar" ? "إزالة" : "Remove"}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {items.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={onPick}
                data-testid="posts-composer-add-image"
                className="aspect-square rounded-lg border-2 border-dashed border-[#c9a55c]/50
                           bg-[#fbf8ef]/60 hover:bg-[#fbf8ef] text-[#8a6f2e] flex flex-col
                           items-center justify-center gap-1 transition-colors"
              >
                <ImagePlus className="w-6 h-6" />
                <span className="text-[10px] font-bold">
                  {lang === "ar" ? "إضافة" : "Add"}
                </span>
              </button>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFiles}
            className="hidden"
          />

          <div className="text-[11px] text-[#8a8a8a]">
            {lang === "ar"
              ? `${items.length}/${MAX_IMAGES} صور — الصورة الأولى ستظهر كغلاف.`
              : `${items.length}/${MAX_IMAGES} images — the first becomes the cover.`}
          </div>
        </div>

        <div className="border-t border-[#0a0a0a]/[0.06] p-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-bold border border-[#0a0a0a]/15 hover:bg-black/5"
          >
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={publish}
            disabled={!canPublish}
            data-testid="posts-composer-publish"
            className="px-5 py-2 rounded-full text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
          >
            {publishing && <Loader2 className="w-4 h-4 animate-spin" />}
            {lang === "ar" ? "نشر" : "Publish"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Lightbox (viewer) ────────────────────────────────────────────── */
function PostLightbox({ post, startIndex, lang, onClose }) {
  const [idx, setIdx] = useState(startIndex || 0);
  const total = post.images?.length || 0;
  const touchStartX = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(total - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      // In RTL, natural swipe direction is inverted for "next".
      const isRtl = document.documentElement.dir === "rtl" || lang === "ar";
      const next = isRtl ? dx > 0 : dx < 0;
      if (next) setIdx((i) => Math.min(total - 1, i + 1));
      else      setIdx((i) => Math.max(0, i - 1));
    }
    touchStartX.current = null;
  };

  const cur = post.images?.[idx];
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="posts-lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* iter334 — Close button pinned as `absolute` at top-end so it
          never gets swallowed by the top-of-viewport flex row (which on
          iOS Safari can be pushed under the notch / dynamic island).
          `env(safe-area-inset-top)` is added as inline style so the
          button always lands BELOW the status bar. z-index inside the
          lightbox keeps it above the image at all times. */}
      <button
        type="button"
        onClick={onClose}
        data-testid="posts-lightbox-close"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 10px)",
        }}
        className="absolute end-3 z-10 inline-flex items-center gap-1.5 px-4 py-3 rounded-full
                   bg-black/75 hover:bg-black/90 border border-white/30 text-white
                   text-[13px] font-bold shadow-[0_4px_18px_rgba(0,0,0,0.55)]
                   active:scale-95 transition"
        aria-label={lang === "ar" ? "إغلاق" : "Close"}
      >
        <X className="w-4 h-4" />
        <span>{lang === "ar" ? "إغلاق" : "Close"}</span>
      </button>

      {/* Image counter — kept but pushed down under the safe area too. */}
      <div
        className="absolute start-4 z-10 text-white text-[12px] opacity-80"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 18px)" }}
      >
        {idx + 1} / {total}
      </div>

      <div className="flex-1 relative flex items-center justify-center p-2 sm:p-6 pt-16">
        {cur && (
          <img
            src={cur}
            alt=""
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />
        )}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="absolute start-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              aria-label={lang === "ar" ? "السابق" : "Previous"}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
              disabled={idx === total - 1}
              className="absolute end-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              aria-label={lang === "ar" ? "التالي" : "Next"}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
      {(post.caption || total > 1) && (
        <div className="px-5 py-3 text-white/90 text-sm max-w-3xl mx-auto w-full">
          {post.caption && <div className="mb-2 leading-relaxed">{post.caption}</div>}
          {total > 1 && (
            <div className="flex gap-1 justify-center">
              {post.images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6 bg-[#e8c873]" : "w-1.5 bg-white/40 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
